// Story 7.12 (ADO-911) — recommendations in a Zustand store (AC1/AC2/AC3).
//
// project-context React rules (ADR-004): Zustand for all client-side global
// state. Recommendations are fetched LAZILY and keyed by SKU: a card loads its
// recommendations ONLY when the "Related products" section is revealed (D-5) —
// never eagerly for every card in a grid. The store caches per SKU so the reveal
// and both rails share ONE fetch.
//
// Both rails come back in one entry (`related` + `customersWhoBought`). AC3 (a
// rail is simply absent) derives from the loaded entry: `loaded` + a non-empty
// array → that rail renders; `loaded` + empty → that rail renders nothing. The two
// rails are independent. Availability-based composition of a tile that is itself
// out of stock is applied by the rail from the availability store, not here.
//
// Copy of `useSubstitutesStore` (generation-stamp guard, per-SKU cache, lazy load)
// — the only shape difference is the entry holds two lists, not one.
//
// Enabler E7-C (ADO-2274), D-1: the request names the selected ordering entry, and
// the cache is reset when the entry changes (`startCatalogueEntryResets`). The depot
// stamp below stays: a depot switch moves the entry too, and the stamp is what the
// rail's `atDepot` read compares.

import { create } from 'zustand';
import { fetchRecommendations, type RecommendedProduct } from '../api/recommendations.client';
import { resolveSessionDepotId } from '../availability/session-depot';
import { resolveCatalogueEntryKey } from '../ordering/catalogueEntry';

export type RecommendationsPhase = 'loading' | 'loaded' | 'error';

export interface RecommendationsEntry {
  readonly phase: RecommendationsPhase;
  /** Related/linked products (FR106). Empty on `loaded` → AC3 no related rail. */
  readonly related: readonly RecommendedProduct[];
  /** Co-purchased products (FR107). Empty on `loaded` → AC3 no bought slider. */
  readonly customersWhoBought: readonly RecommendedProduct[];
  /** The depot these recommendations were fetched at (`null` = no active depot).
   *  DEPOT-AWARE, mirroring `useSubstitutesStore` (7-FU-I review): `fetchRecommendations`
   *  is depot-scoped, and a header depot switch flips the active depot WITHOUT resetting
   *  this store, so `load()` compares this stamp to the current depot and refetches when
   *  they differ rather than early-returning on a cached entry for the OLD depot. */
  readonly depotId: string | null;
}

interface RecommendationsStoreState {
  /** Per-SKU cache. Absent = never requested (no fetch yet). */
  readonly bySku: Readonly<Record<string, RecommendationsEntry>>;
  /**
   * Lazily load `sku`'s recommendations (idempotent). Already loaded → resolves
   * at once; already IN FLIGHT → resolves when that fetch settles (see
   * `inFlight` below); a prior `error` entry may retry. Resolves the session
   * depot itself so callers just pass the SKU.
   */
  load: (sku: string) => Promise<void>;
  /** Test/session hygiene — drop all state. */
  reset: () => void;
}

const EMPTY: Pick<RecommendationsEntry, 'related' | 'customersWhoBought'> = {
  related: [],
  customersWhoBought: [],
};

function withEntry(
  bySku: Readonly<Record<string, RecommendationsEntry>>,
  sku: string,
  entry: RecommendationsEntry,
): Readonly<Record<string, RecommendationsEntry>> {
  return { ...bySku, [sku]: entry };
}

// A monotonic generation stamp. `reset()` bumps it; a `load()` captures it before
// awaiting and only commits its result if the stamp is unchanged — so a fetch that
// resolves AFTER a reset() (logout / test hygiene) cannot resurrect a stale entry
// into the freshly-cleared cache. Mirrors the substitutes/availability stores.
let generation = 0;

// Per-SKU load generation (7-FU-I review). `generation` above only moves on `reset()`, so
// it cannot separate two concurrent loads for the SAME sku at DIFFERENT depots: a header
// depot switch re-invokes `load()` while the OLD depot's fetch may still be in flight, and
// nothing abandons it. If the slower OLD-depot fetch settled AFTER the new one it would
// commit its lists stamped with the OLD depot and win permanently (nothing re-invokes
// `load()` after that). Unlike `useSubstitutesStore` there is no retry cancel token to reuse,
// so we bump a per-SKU token on each fresh cycle and drop any commit whose token has been
// superseded — only the LATEST `load()`'s result lands, regardless of settle order.
let loadGeneration: Record<string, number> = {};

// In-flight fetches, keyed by SKU. `load()` must be AWAITABLE, not merely
// idempotent: `useUpsellModalStore.openUpsell` awaits it and then reads
// `bySku[sku].related` to decide whether to open (AC9). Early-returning on a
// `loading` entry made that read hit the EMPTY placeholder, so an Add clicked
// while the PDP's own mount-fetch was still in flight looked exactly like "no
// linked products" and the modal silently never opened — breaking AC7 for the
// whole duration of the request. Callers now join the existing promise instead.
// Each entry is cleared when ITS OWN request settles (identity-checked, so a
// slow request cannot evict a newer one) and before that promise resolves, so a
// settled SKU can retry from its `error` entry.
//
// A depot switch never joins this map — see `depotChanged` in `load()`: the pending
// promise belongs to the OLD depot, so joining it would hand the caller the very
// result the depot-aware refetch exists to discard.
const inFlight = new Map<string, Promise<void>>();

export const useRecommendationsStore = create<RecommendationsStoreState>((set, get) => ({
  bySku: {},

  async load(sku) {
    // Enabler E7-C (ADO-2274), D-1: the rails are read in the selected entry's range. With no
    // entry there is nothing to ask, and no section renders; the page re-invokes `load` once
    // one is chosen.
    const entry = resolveCatalogueEntryKey();
    if (entry === null) return;
    const depotId = resolveSessionDepotId();
    const existing = get().bySku[sku];
    // Depot-aware refetch (7-FU-I review), mirroring useSubstitutesStore: a header depot
    // switch flips the active depot WITHOUT resetting this store and entries are keyed by SKU
    // alone, so a cached `loaded`/`loading` entry for the OLD depot would early-return and
    // leave the rails showing the wrong depot's recommendations. Refetch when the depot differs.
    const depotChanged = existing !== undefined && existing.depotId !== depotId;

    // Both early-exits are depot-gated. A changed depot falls through to a fresh cycle: it
    // must neither honour a cached OLD-depot entry nor join an OLD-depot request still in
    // flight — that pending promise resolves to precisely the result we are refetching to
    // replace. The superseded() token below then discards its commit if it lands late.
    if (!depotChanged) {
      if (existing?.phase === 'loaded') return;
      // Already fetching this SKU at THIS depot — await THAT fetch rather than returning
      // early, so every caller observes a settled entry (AC9).
      const pending = inFlight.get(sku);
      if (pending !== undefined) return pending;
    }

    const gen = generation;
    // Claim this SKU's latest-load token; a commit below stands only while it is unchanged.
    const loadGen = (loadGeneration[sku] ?? 0) + 1;
    loadGeneration[sku] = loadGen;
    // Superseded by a later load() for this SKU (a depot switch), or by a reset().
    const superseded = (): boolean => gen !== generation || loadGen !== loadGeneration[sku];

    set({ bySku: withEntry(get().bySku, sku, { phase: 'loading', ...EMPTY, depotId }) });

    const fetched = (async () => {
      try {
        // `depotId` is the one resolved at the TOP of this cycle, not re-read here: the
        // placeholder above, the request, and the commit's stamp must all describe the
        // same depot, or a switch landing mid-call would stamp a result with a depot it
        // was not fetched at and defeat the `depotChanged` comparison on the next load.
        const { related, customersWhoBought } = await fetchRecommendations(entry, sku);
        if (superseded()) return; // a reset() or a newer load() for this SKU — drop the stale result
        set({
          bySku: withEntry(get().bySku, sku, {
            phase: 'loaded',
            related,
            customersWhoBought,
            depotId,
          }),
        });
      } catch {
        if (superseded()) return; // a reset() or a newer load() for this SKU — drop the stale error
        // A fetch failure must not crash the card — flag the error so the section can
        // offer a retry rather than a permanent spinner, leaving no stale lists.
        set({ bySku: withEntry(get().bySku, sku, { phase: 'error', ...EMPTY, depotId }) });
      }
    })();

    // Clear ONLY OUR OWN key. `reset()` clears the whole map, so a request that
    // was already running would otherwise evict whatever now sits under its SKU —
    // and by then that can be a NEWER promise for the same SKU. The entry would be
    // left `loading` with no `inFlight` record, and since the `phase === 'loading'`
    // early-return is gone, the next `load(sku)` would fire a duplicate fetch and
    // overwrite it with a fresh `loading` placeholder.
    //
    // The cleanup is chained rather than a `finally` BLOCK inside the async body,
    // and the CHAINED promise is what goes in the map: a `.finally` callback can
    // only run as a microtask, so it cannot observe `request` before this
    // statement completes (a `finally` block would, on a synchronous throw), and
    // every caller — the originator and any joiner — awaits a promise that settles
    // only after the map has been tidied, so an `error` entry can retry at once.
    const request: Promise<void> = fetched.finally(() => {
      if (inFlight.get(sku) === request) inFlight.delete(sku);
    });

    inFlight.set(sku, request);
    return request;
  },

  reset() {
    generation += 1;
    loadGeneration = {};
    inFlight.clear();
    set({ bySku: {} });
  },
}));
